import { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import NavBar from "../../Componentes/NavBar";
import SortableTable from "../../Componentes/Utils/SortableTable";
import GenericHeader from "../../Componentes/GenericHeader";
import Filters from "../../Componentes/Utils/Filters";
import FilterContainer from "../../Componentes/Utils/FilterContainer";
import MultiselectCheck from "../../Componentes/Utils/MultiselectCheck";
import { useModal } from "../../contexts/ModalContext";
import useApi from "../../hooks/useApi";
import SidebarLicencasColaboradores from "./SidebarLicencasColaboradores";
import ModalNovoFornecLicenca from "./ModalNovoFornecLicenca";
import ModalNovoTipoLicenca from "./ModalNovoTipoLicenca";
import ModalLancarLicencaColaborador from "./ModalLancarLicencaColaborador";
import ModalDetalhesLicencasColaborador from "./ModalDetalhesLicencasColaborador";

const FORNECEDORES_ENDPOINT = "/Painel/API/InventarioTI/GetFornecLicenca/";
const COLABS_LICENCA_ENDPOINT = "/Painel/API/InventarioTI/GetColabsLicenca/";
const RELATORIO_LICENCAS_ENDPOINT = "/Painel/API/InventarioTI/GerarRelatorioLicencas/";

const dash = (value) => {
  if (value == null || String(value).trim() === "") return "—";
  return String(value).trim();
};

const normalizeMatricula = (value) => {
  const digits = String(value ?? "").replace(/\D/g, "");
  if (!digits) return "";
  return digits.padStart(6, "0").slice(-6);
};

const Container = styled.div`
  width: 100%;
  height: 100dvh;
  background-color: #242d34;
  display: flex;
  flex-direction: column;
`;

const BodyWrapper = styled.div`
  width: 100%;
  flex: 1;
  padding: 10px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  gap: 10px;
`;

const Statistics = styled.div`
  width: 280px;
  flex-shrink: 0;
  background-color: #f3f3f3;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 12px;
  padding: 10px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const ContainerStatistic = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  justify-content: center;
  min-height: 100%;
`;

const TextoStatistic = styled.p`
  margin: 0;
  padding: 0;
  font-size: 13px;
  color: #333;
`;

const TableShell = styled.div`
  width: 100%;
  flex: 1;
  min-height: 0;
  background-color: #ffffff;
  border-radius: 15px;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  & > div {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
`;

const StatusDot = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${(p) => {
    if (p.$deAcordo) return "#eab308";
    if (p.$ativo) return "#22c55e";
    return "#ef4444";
  }};
  flex-shrink: 0;
`;

const StatusCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

export default function LicencasColaboradores() {
  const { openModal, isModalOpen, closeModal } = useModal();
  const api = useApi();

  const [loading, setLoading] = useState(true);
  const [loadingRelatorio, setLoadingRelatorio] = useState(false);
  const [fornecedores, setFornecedores] = useState([]);
  const [rows, setRows] = useState([]);
  const [nomeColab, setNomeColab] = useState("");
  const [matColab, setMatColab] = useState("");
  const [selectedFunc, setSelectedFunc] = useState([]);
  const [selectedObra, setSelectedObra] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [filtroLicenca, setFiltroLicenca] = useState("");
  const [selectedColaborador, setSelectedColaborador] = useState(null);

  const requestColaboradores = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(COLABS_LICENCA_ENDPOINT);
      const list = Array.isArray(data) ? data : data?.data ?? [];
      setRows(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || "Erro ao carregar colaboradores.");
    } finally {
      setLoading(false);
    }
  }, [api]);

  const requestFornecedores = useCallback(async () => {
    try {
      const { data } = await api.get(FORNECEDORES_ENDPOINT);
      const list = Array.isArray(data) ? data : data?.data ?? [];
      setFornecedores(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error(err);
    }
  }, [api]);

  useEffect(() => {
    requestColaboradores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const colaboradores = useMemo(() => {
    return (rows ?? []).map((r) => {
      const centroCustoDesc =
        r.centro_custo_desc != null && String(r.centro_custo_desc).trim() !== ""
          ? String(r.centro_custo_desc).trim()
          : "";
      const centroCusto =
        r.centro_custo != null && String(r.centro_custo).trim() !== ""
          ? String(r.centro_custo).trim()
          : "";

      return {
        matricula: dash(r.matricula),
        nome: dash(r.nome),
        funcao: dash(r.funcao),
        centro_custo: centroCustoDesc || centroCusto || "—",
        qtd_licencas: Number(r.qtd_licencas ?? 0),
        licencas: Array.isArray(r.licencas) ? r.licencas : [],
        demitido: Boolean(r.demitido),
        de_acordo: Boolean(r.de_acordo),
        status: Boolean(r.demitido) ? "DESLIGADO" : "ATIVO",
      };
    });
  }, [rows]);

  const funcoes = useMemo(
    () =>
      [...new Set(colaboradores.map((item) => item.funcao).filter((f) => f && f !== "—"))].sort(),
    [colaboradores]
  );

  const obras = useMemo(
    () =>
      [
        ...new Set(
          colaboradores.map((item) => item.centro_custo).filter((cc) => cc && cc !== "—")
        ),
      ].sort(),
    [colaboradores]
  );

  const opcoesFuncao = useMemo(
    () => funcoes.map((funcao) => ({ value: funcao, label: funcao })),
    [funcoes]
  );

  const opcoesObra = useMemo(
    () => obras.map((obra) => ({ value: obra, label: obra })),
    [obras]
  );

  const opcoesStatus = useMemo(
    () => [
      { value: "ATIVO", label: "ATIVO" },
      { value: "DESLIGADO", label: "DESLIGADO" },
    ],
    []
  );

  const filtered = useMemo(() => {
    const termoLicenca = filtroLicenca.trim().toUpperCase();

    const base = colaboradores.filter((item) => {
      const name_filter =
        !nomeColab ||
        (item.nome === "—" ? "" : item.nome).toUpperCase().includes(nomeColab.toUpperCase());
      const mat_filter =
        !matColab || normalizeMatricula(item.matricula).includes(matColab.replace(/\D/g, ""));
      const func_filter =
        selectedFunc.length === 0 ||
        selectedFunc.some((func) => (item.funcao || "").includes(func));
      const obra_filter =
        selectedObra.length === 0 ||
        selectedObra.some((obra) => (item.centro_custo || "").includes(obra));
      const stat_filter =
        selectedStatus.length === 0 ||
        selectedStatus.some((stat) => item.status.includes(stat));
      const lic_filter =
        !termoLicenca ||
        (item.licencas ?? []).some((lic) => {
          const fornecedor = String(lic.fornecedor ?? "").toUpperCase();
          const licenca = String(lic.licenca ?? lic.tipo ?? "").toUpperCase();
          return fornecedor.includes(termoLicenca) || licenca.includes(termoLicenca);
        });

      return name_filter && mat_filter && func_filter && obra_filter && stat_filter && lic_filter;
    });

    return [...base].sort((a, b) => {
      const aDesligadoSemAcordo = a.demitido && !a.de_acordo;
      const bDesligadoSemAcordo = b.demitido && !b.de_acordo;

      if (aDesligadoSemAcordo !== bDesligadoSemAcordo) {
        return aDesligadoSemAcordo ? -1 : 1;
      }

      const nomeA = a.nome === "—" ? "" : a.nome;
      const nomeB = b.nome === "—" ? "" : b.nome;
      return nomeA.localeCompare(nomeB, "pt-BR", { sensitivity: "base" });
    });
  }, [colaboradores, filtroLicenca, matColab, nomeColab, selectedFunc, selectedObra, selectedStatus]);

  const totalColaboradores = filtered.length;
  const totalAtivos = filtered.filter((c) => c.status === "ATIVO").length;
  const totalDesligados = filtered.filter((c) => c.status === "DESLIGADO").length;

  const totalLicencas = useMemo(
    () => filtered.reduce((acc, c) => acc + (Number(c.qtd_licencas) || 0), 0),
    [filtered]
  );

  const columns = useMemo(
    () => [
      {
        id: "status",
        header: "",
        accessor: () => null,
        sortable: false,
        width: "44px",
        align: "center",
        render: (_v, row) => {
          const statusTitle = row.de_acordo
            ? "De acordo (desligado apenas no sistema)"
            : row.demitido
            ? "Desligado"
            : "Ativo";

          return (
            <StatusCell>
              <StatusDot
                $ativo={!row.demitido}
                $deAcordo={row.de_acordo}
                title={statusTitle}
                aria-label={statusTitle}
              />
            </StatusCell>
          );
        },
      },
      {
        id: "matricula",
        header: "MATRÍCULA",
        accessor: (r) => r.matricula,
        width: "110px",
      },
      {
        id: "nome",
        header: "NOME",
        accessor: (r) => r.nome,
        width: "260px",
      },
      {
        id: "funcao",
        header: "FUNÇÃO",
        accessor: (r) => r.funcao,
        width: "220px",
      },
      {
        id: "centro_custo",
        header: "CENTRO DE CUSTO",
        accessor: (r) => r.centro_custo,
        width: "240px",
      },
      {
        id: "qtd_licencas",
        header: "QTD. LICENÇAS",
        accessor: (r) => r.qtd_licencas,
        sortValue: (r) => Number(r.qtd_licencas) || 0,
        width: "130px",
        align: "center",
      },
    ],
    []
  );

  const handleOpenSidebar = () => openModal("sidebarLicencasColaboradores");

  const handleLancarLicenca = () => {
    closeModal();
    openModal("modalLancarLicencaColaborador");
  };

  const handleNovoFornecedor = () => {
    closeModal();
    openModal("modalNovoFornecLicenca");
  };

  const handleNovoTipoLicenca = () => {
    closeModal();
    openModal("modalNovoTipoLicenca");
  };

  const handleFornecedorCreated = async () => {
    await requestFornecedores();
  };

  const handleLicencaLancada = async () => {
    await requestColaboradores();
  };

  const handleLicencasChanged = async () => {
    await requestColaboradores();
  };

  const handleRowClick = (row) => {
    setSelectedColaborador(row);
    openModal("modalDetalhesLicencasColaborador");
  };

  const gerarRelatorio = async () => {
    if (loadingRelatorio) return;

    if (filtered.length === 0) {
      alert("Nenhum colaborador encontrado com os filtros atuais.");
      return;
    }

    setLoadingRelatorio(true);

    try {
      const payload = filtered.map((c) => ({
        matricula: normalizeMatricula(c.matricula),
        demitido: Boolean(c.demitido),
      }));

      const response = await api.post(RELATORIO_LICENCAS_ENDPOINT, payload, {
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const stamp = new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/[-:T]/g, "")
        .slice(0, 14);
      link.href = url;
      link.setAttribute("download", `licencas_colaboradores_${stamp}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      closeModal();
    } catch (err) {
      console.error(err);
      let msg = "Erro ao gerar relatório.";
      if (err?.response?.data instanceof Blob) {
        try {
          const parsed = JSON.parse(await err.response.data.text());
          if (parsed?.message) msg = parsed.message;
        } catch {
          /* mantém mensagem padrão */
        }
      } else if (err?.response?.data?.message) {
        msg = err.response.data.message;
      }
      alert(msg);
    } finally {
      setLoadingRelatorio(false);
    }
  };

  return (
    <>
      <Container>
        <NavBar
          title="INVENTÁRIO T.I. — LICENÇAS"
          navTo="/modulo/inventario-ti"
          setSideBar={handleOpenSidebar}
        />
        <BodyWrapper>
          <GenericHeader responsive>
            <Filters columns={3}>
              <FilterContainer
                type="text"
                placeholder="COLABORADOR..."
                valor={nomeColab}
                changeFunc={setNomeColab}
              />
              <FilterContainer
                type="text"
                placeholder="MATRICULA..."
                valor={matColab}
                changeFunc={setMatColab}
              />
              <FilterContainer
                type="text"
                placeholder="LICENÇA / FORNECEDOR..."
                valor={filtroLicenca}
                changeFunc={setFiltroLicenca}
              />
              <MultiselectCheck
                options={opcoesFuncao}
                selectedOptions={selectedFunc}
                setSelectedOptions={setSelectedFunc}
                placeholder="FUNÇÃO..."
              />
              <MultiselectCheck
                options={opcoesObra}
                selectedOptions={selectedObra}
                setSelectedOptions={setSelectedObra}
                placeholder="CENTRO CUSTO..."
              />
              <MultiselectCheck
                options={opcoesStatus}
                selectedOptions={selectedStatus}
                setSelectedOptions={setSelectedStatus}
                placeholder="STATUS..."
              />
            </Filters>

            <Statistics>
              <ContainerStatistic>
                <TextoStatistic>COLABORADORES: {totalColaboradores}</TextoStatistic>
                <TextoStatistic>
                  ATIVOS: {totalAtivos}&nbsp;&nbsp;&nbsp;DESLIGADOS: {totalDesligados}
                </TextoStatistic>
                <TextoStatistic>LICENÇAS ATIVAS: {totalLicencas}</TextoStatistic>
              </ContainerStatistic>
            </Statistics>
          </GenericHeader>

          <TableShell>
            <SortableTable
              columns={columns}
              data={filtered}
              isLoading={loading}
              loadingText="Carregando colaboradores..."
              onRowClick={handleRowClick}
              rowCursor="pointer"
              emptyText="NENHUM COLABORADOR COM LICENÇA ENCONTRADO"
            />
          </TableShell>
        </BodyWrapper>
      </Container>

      {isModalOpen("sidebarLicencasColaboradores") && (
        <SidebarLicencasColaboradores
          onClose={() => closeModal()}
          onLancarLicenca={handleLancarLicenca}
          onNovoFornecedor={handleNovoFornecedor}
          onNovoTipoLicenca={handleNovoTipoLicenca}
          onGerarRelatorio={gerarRelatorio}
          loadingRelatorio={loadingRelatorio}
        />
      )}

      {isModalOpen("modalDetalhesLicencasColaborador") && selectedColaborador && (
        <ModalDetalhesLicencasColaborador
          colaborador={selectedColaborador}
          onClose={() => {
            closeModal();
            setSelectedColaborador(null);
          }}
          onChanged={handleLicencasChanged}
        />
      )}

      {isModalOpen("modalLancarLicencaColaborador") && (
        <ModalLancarLicencaColaborador
          onClose={() => closeModal()}
          onSaved={handleLicencaLancada}
        />
      )}

      {isModalOpen("modalNovoFornecLicenca") && (
        <ModalNovoFornecLicenca
          onClose={() => closeModal()}
          onCreated={handleFornecedorCreated}
        />
      )}

      {isModalOpen("modalNovoTipoLicenca") && (
        <ModalNovoTipoLicenca
          onClose={() => closeModal()}
          onCreated={handleFornecedorCreated}
          fornecedores={fornecedores}
          onRequestFornecedores={requestFornecedores}
        />
      )}
    </>
  );
}
